import _ from 'lodash'
import * as yup from 'yup'
import { ResolvedTableCellData, isResolvedTableCellData } from './table-cell.js'

export const ROW_RELATION_TYPES = {
  PARENT_ROW: 'parent_row',
  CHILD_ROW: 'child_row',
} as const

export type RowRelationType = (typeof ROW_RELATION_TYPES)[keyof typeof ROW_RELATION_TYPES]

export const userSchema = yup.object({
  id: yup.string().required(),
  firstName: yup.string().required(),
  lastName: yup.string().defined(),
  email: yup.string().nullable().defined(),
  phoneNumber: yup.string().nullable().defined(),
})

export const userReferenceSchema = yup.object({
  id: yup.string().required(),
  firstName: yup.string().required(),
  lastName: yup.string().defined(),
  email: yup.string().nullable().defined(),
  phoneNumber: yup.string().nullable().defined(),
  formattedValue: yup.string().required(),
  sortValue: yup.string().required(),
})

export type UserReference = yup.InferType<typeof userReferenceSchema>

export const resolvedTableCellSchema = yup
  .object({
    id: yup.string().required(),
    columnId: yup.string().required(),
    rowId: yup.string().required(),
    createdAt: yup.date().required(),
    updatedAt: yup.date().required(),

    groupId: yup.string().nullable().defined(),

    resolvedData: yup
      .mixed<ResolvedTableCellData>((value): value is ResolvedTableCellData =>
        isResolvedTableCellData(value),
      )
      .nullable()
      .defined(),
    formattedValue: yup.string().nullable().defined(),
    formulaError: yup.number().nullable().defined(),
    lookupCells: yup
      .array(
        yup
          .object({
            id: yup.string().required(),
            rowId: yup.string().required(),
            columnId: yup.string().required(),
          })
          .required(),
      )
      .nullable()
      .defined(),
    documents: yup
      .array(
        yup
          .object({
            id: yup.string().required(),
            name: yup.string().required(),
            /**
             * Optional rather than defined: this shape is also the Elasticsearch document, and
             * rows indexed before these fields existed simply do not carry them. Modelling them
             * as always-present would have the type assert a value that isn't in the index.
             * Absent and null both mean "unknown", and callers should treat them the same.
             */
            contentSha256: yup.string().nullable().optional(),
            contentSizeBytes: yup.number().nullable().optional(),
            documentType: yup.string().nullable().optional(),
          })
          .required(),
      )
      .nullable()
      .defined(),
    users: yup.array(userSchema).nullable().defined(),
  })
  .strict()

/**
 * Represents a fully resolved (denormalized) table cell.
 */
export type ResolvedTableCell = yup.InferType<typeof resolvedTableCellSchema>

/**
 * Represents table cell data stored in the `nestedCells` field of a table row document in
 * Elasticsearch.
 */
export const nestedTableCellSchema = yup
  .object({
    columnId: yup.string().required(),

    formattedValue: yup.string().nullable().defined(),
    // Smallint formula error code, mirrors `ResolvedTableCell.formulaError`. Only populated for
    // formula-typed columns; null when the formula has no error.
    error_code: yup.number().nullable().defined(),
    data_string: yup
      .mixed<string | string[]>((value): value is string | string[] => {
        return yup.string().isValidSync(value) || yup.array().of(yup.string()).isValidSync(value)
      })
      .notRequired(),
    data_number: yup
      .mixed<number | number[]>((value): value is number | number[] => {
        return yup.number().isValidSync(value) || yup.array().of(yup.number()).isValidSync(value)
      })
      .notRequired(),
    data_boolean: yup
      .mixed<boolean | boolean[]>((value): value is boolean | boolean[] => {
        return yup.boolean().isValidSync(value) || yup.array().of(yup.boolean()).isValidSync(value)
      })
      .notRequired(),
    data_date: yup
      .mixed<string | string[]>((value): value is string | string[] => {
        return (
          yup.string().datetime().isValidSync(value) ||
          yup.array().of(yup.string().datetime()).isValidSync(value)
        )
      })
      .notRequired(),

    lookupCells: yup
      .array(
        yup.object({
          rowId: yup.string().required(),
        }),
      )
      .nullable()
      .defined(),

    users: yup
      .array(
        yup.object({
          id: yup.string().required(),
        }),
      )
      .nullable()
      .defined(),
  })
  .strict()

/**
 * Represents table cell data stored in the `nestedCells` field of a table row document in
 * Elasticsearch.
 */
export type NestedTableCell = yup.InferType<typeof nestedTableCellSchema>

export type ResolvedTableCellLookupCell = {
  columnId: string
  rowId: string
  id: string
}

export type ResolvedTableCellDocument = {
  id: string
  name: string
  /**
   * Null when the document has no stored fingerprint, or when this row was indexed into
   * Elasticsearch before the field existed. Both mean "unknown".
   */
  contentSha256?: string | null
  contentSizeBytes?: number | null
  documentType?: string | null
}

export type ResolvedTableCellUser = {
  id: string
  firstName: string
  lastName: string
  email?: string | null
  phoneNumber?: string | null
}

const joinRelationSchema = yup.lazy((value) => {
  // If it's a string, it should be 'parent_row'
  if (typeof value === 'string') {
    return yup
      .string()
      .oneOf([ROW_RELATION_TYPES.PARENT_ROW] as const)
      .required()
  }

  // If it's an object, it should be for child_row with parent field
  return yup
    .object({
      name: yup
        .string()
        .oneOf([ROW_RELATION_TYPES.CHILD_ROW] as const)
        .required(),
      parent: yup.string().required(),
    })
    .strict()
})

/**
 * Represents a table row with fully resolved (denormalized) table cells. This is the format that
 * table rows are stored in Elasticsearch.
 */
export const resolvedTableRowSchema = yup
  .object({
    id: yup.string().required(),
    version: yup.number().required(),

    tableId: yup.string().required(),
    rootWorkspaceId: yup.string().required(),
    parentRowId: yup.string().nullable().defined(),

    createdAt: yup.date().required(),
    updatedAt: yup.date().required(),
    createdBy: userReferenceSchema.nullable().defined(),
    updatedBy: userReferenceSchema.nullable().defined(),
    createdById: yup.string().nullable().defined(),
    updatedById: yup.string().nullable().defined(),
    name: yup.string().required(),

    rowRelation: joinRelationSchema,

    cells: yup.lazy((obj) =>
      yup
        .object()
        .shape(_.mapValues(obj, () => resolvedTableCellSchema.required()))
        .required(),
    ),
    nestedCells: yup.array().of(nestedTableCellSchema).required(),
  })
  .strict()

/**
 * Represents a table row with fully resolved (denormalized) table cells. This is the format that
 * table rows are stored in Elasticsearch.
 */
export type ResolvedTableRow = yup.InferType<typeof resolvedTableRowSchema>
